/// <reference types="vite/client" />
// ─────────────────────────────────────────────────────────────────────────────
// LIVE AR FASHION OS — Production TryOn Page
//
// Stack (web client layer of the full AR OS):
//   Camera → MediaPipe Pose (33 joints) → MediaPipe Segmentation (occlusion)
//   → Three.js WebGL renderer (shader-warped garment plane)
//   → Full-screen selfie mirror + floating bottom drawer UI
//
// Degradation tiers:
//   Tier 1 — Full:    Pose + Occlusion + WebGL warp
//   Tier 2 — Partial: Pose + WebGL warp, no occlusion
//   Tier 3 — Basic:   Heuristic position, no MediaPipe
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState, useCallback } from "react";
import {
  RefreshCw, Zap, ChevronLeft, ChevronRight,
  Star, Check, AlertCircle, Loader2, Shirt, Sparkles,
  SlidersHorizontal, Download, Share2, RotateCcw,
  ChevronDown, Aperture, Sun, Contrast, Layers,
  X, Activity, Eye, EyeOff, Wind,
} from "lucide-react";
import { clsx } from "clsx";
import { api } from "../services/api";
import { ARPipeline, type ARTier, type ARStatus } from "../lib/ar/pipeline";
import { getPoseLandmarker } from "../lib/ar/pose";
import { getSegmenter } from "../lib/ar/segmenter";

// ── Types ────────────────────────────────────────────────────────────────────
interface Garment {
  id: string;
  name: string;
  brand?: string;
  category: string;
  image_url?: string;
  price_cents?: number;
  color?: string;
}

interface TryOnResult {
  session_id: string;
  result_image_url: string;
  quality_score: number;
  fit_details: Record<string, string>;
  size: string;
  provider: string;
  latency_ms: number;
}

interface AiOutfit {
  title: string;
  description: string;
  mood: string;
  tags: string[];
  emoji: string;
  garmentId?: string;
}

type DrawerMode = "mini" | "garments" | "ai" | "settings";
type ProcessingState = "idle" | "scanning" | "fitting" | "done" | "error";

// ── AR tier badge ─────────────────────────────────────────────────────────────
const TIER_LABEL: Record<ARTier, { label: string; color: string }> = {
  1: { label: "AR FULL", color: "text-green-400 border-green-400/40 bg-green-400/10" },
  2: { label: "AR POSE", color: "text-yellow-400 border-yellow-400/40 bg-yellow-400/10" },
  3: { label: "AR BASIC", color: "text-orange-400 border-orange-400/40 bg-orange-400/10" },
};

// ── Mock AI outfits ───────────────────────────────────────────────────────────
const AI_OUTFITS: AiOutfit[] = [
  { emoji: "🔥", title: "Urban Edge", description: "Streetwear-forward, bold silhouette for city energy", mood: "Confident", tags: ["street", "bold", "oversized"] },
  { emoji: "✨", title: "Clean Minimal", description: "Tonal neutrals — effortless structure, zero noise", mood: "Focused", tags: ["minimal", "neutral", "clean"] },
  { emoji: "💼", title: "Smart Casual", description: "Office-ready without the rigidity — elevated comfort", mood: "Polished", tags: ["smart", "versatile", "elevated"] },
  { emoji: "🌅", title: "Weekend Soft", description: "Relaxed fits, muted tones — your day off look", mood: "Relaxed", tags: ["soft", "comfort", "muted"] },
  { emoji: "🎯", title: "Statement Piece", description: "One loud piece, everything else quiet — let it land", mood: "Bold", tags: ["statement", "editorial", "impact"] },
];

// ── Fit badge color ──────────────────────────────────────────────────────────
function fitColor(val: string) {
  if (val === "perfect" || val === "good") return "text-green-400";
  if (val === "fitted") return "text-blue-400";
  if (val === "relaxed") return "text-yellow-400";
  return "text-orange-400";
}

// ─────────────────────────────────────────────────────────────────────────────
// GARMENT THUMBNAIL
// ─────────────────────────────────────────────────────────────────────────────
function GarmentThumb({ garment, selected, onSelect }: {
  garment: Garment; selected: boolean; onSelect: () => void;
}) {
  const [err, setErr] = useState(false);
  return (
    <button
      onClick={onSelect}
      className={clsx(
        "relative shrink-0 w-[88px] rounded-2xl border-2 overflow-hidden transition-all duration-200",
        selected
          ? "border-sxt-primary ring-2 ring-sxt-primary/30 scale-[1.06]"
          : "border-white/10 hover:border-white/25 hover:scale-[1.02]"
      )}
    >
      <div className="aspect-[3/4] bg-white/5 flex items-center justify-center">
        {garment.image_url && !err ? (
          <img src={garment.image_url} alt={garment.name}
            className="w-full h-full object-cover"
            onError={() => setErr(true)} />
        ) : (
          <span className="text-3xl">👕</span>
        )}
      </div>
      <div className="px-1.5 py-1 bg-black/70 backdrop-blur-sm">
        <p className="text-[10px] text-white font-semibold truncate leading-tight">{garment.name}</p>
        {garment.price_cents ? (
          <p className="text-[10px] text-sxt-primary font-bold">${(garment.price_cents / 100).toFixed(0)}</p>
        ) : (
          <p className="text-[10px] text-white/30">{garment.category}</p>
        )}
      </div>
      {selected && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-sxt-primary rounded-full flex items-center justify-center shadow-lg">
          <Check className="w-3 h-3 text-black" />
        </div>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AI OUTFIT CARD
// ─────────────────────────────────────────────────────────────────────────────
function AiCard({ outfit, onApply }: { outfit: AiOutfit; onApply: () => void }) {
  return (
    <div className="shrink-0 w-44 bg-white/5 rounded-2xl border border-white/10 p-3.5 flex flex-col gap-2">
      <div className="text-2xl">{outfit.emoji}</div>
      <div>
        <p className="text-sm font-bold leading-tight">{outfit.title}</p>
        <p className="text-[10px] text-sxt-muted mt-0.5 italic">{outfit.mood}</p>
      </div>
      <p className="text-xs text-white/50 leading-relaxed flex-1">{outfit.description}</p>
      <div className="flex flex-wrap gap-1">
        {outfit.tags.map(t => (
          <span key={t} className="text-[9px] bg-sxt-primary/15 text-sxt-primary px-1.5 py-0.5 rounded-full font-medium">
            {t}
          </span>
        ))}
      </div>
      <button onClick={onApply}
        className="w-full py-2 bg-sxt-primary text-black text-xs font-bold rounded-xl hover:bg-sxt-primary/90 transition-all mt-auto">
        Apply Look
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RESULT OVERLAY
// ─────────────────────────────────────────────────────────────────────────────
function ResultOverlay({ capturedImage, result, selectedGarment, onRetake, onTryAnother }: {
  capturedImage: string;
  result: TryOnResult;
  selectedGarment?: Garment;
  onRetake: () => void;
  onTryAnother: () => void;
}) {
  const [rating, setRating] = useState(0);

  return (
    <div className="absolute inset-0 z-40 bg-black/92 backdrop-blur-xl flex flex-col overflow-y-auto overscroll-contain">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-5 pb-3 shrink-0">
        <button onClick={onRetake} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all">
          <X className="w-5 h-5" />
        </button>
        <div className="text-center">
          <p className="text-sm font-bold">Try-On Result</p>
          <p className="text-xs text-sxt-muted">
            {result.latency_ms ? `${(result.latency_ms / 1000).toFixed(1)}s · ` : ""}
            {result.provider || "AI Engine"}
          </p>
        </div>
        <div className="flex gap-1.5">
          <button className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all">
            <Share2 className="w-4 h-4" />
          </button>
          <button className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all">
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Before / After */}
      <div className="grid grid-cols-2 gap-2 px-3 shrink-0">
        <div className="relative rounded-2xl overflow-hidden">
          <span className="absolute top-2 left-2 z-10 text-[10px] bg-black/60 rounded-full px-2 py-0.5 font-semibold">You</span>
          <img src={capturedImage} alt="Original" className="w-full aspect-[3/4] object-cover" />
        </div>
        <div className="relative rounded-2xl overflow-hidden bg-sxt-surface">
          <span className="absolute top-2 left-2 z-10 text-[10px] bg-sxt-primary/80 text-black rounded-full px-2 py-0.5 font-bold">Try-On</span>
          {result.result_image_url ? (
            <img src={result.result_image_url} alt="Result" className="w-full aspect-[3/4] object-cover" />
          ) : (
            <div className="w-full aspect-[3/4] flex flex-col items-center justify-center gap-2 p-4">
              {selectedGarment?.image_url && (
                <img src={selectedGarment.image_url} alt="" className="w-2/3 object-contain opacity-50" />
              )}
              <span className="text-xs text-sxt-muted text-center">Full overlay requires IDM-VTON GPU</span>
            </div>
          )}
        </div>
      </div>

      {/* Fit details */}
      {result.fit_details && Object.keys(result.fit_details).length > 0 && (
        <div className="mx-3 mt-3 bg-white/5 rounded-2xl border border-white/10 p-4 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold">Fit Analysis</h3>
            <span className="text-xs bg-sxt-primary/20 text-sxt-primary px-2 py-0.5 rounded-full font-bold">
              Size {result.size}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(result.fit_details).slice(0, 6).map(([region, val]) => (
              <div key={region} className="text-center">
                <p className="text-[10px] text-sxt-muted capitalize mb-1">{region}</p>
                <p className={clsx("text-xs font-semibold capitalize", fitColor(val))}>{val}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 bg-white/10 rounded-full h-1">
              <div className="bg-sxt-primary h-1 rounded-full"
                style={{ width: `${(result.quality_score || 0.85) * 100}%` }} />
            </div>
            <span className="text-[10px] text-sxt-muted">
              {Math.round((result.quality_score || 0.85) * 100)}% confidence
            </span>
          </div>
        </div>
      )}

      {/* Rating */}
      <div className="mx-3 mt-3 bg-white/5 rounded-2xl border border-white/10 p-4 shrink-0">
        <p className="text-sm font-semibold mb-2">How does it look?</p>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} onClick={() => setRating(n)}
              className={clsx("p-1.5 rounded-xl transition-all", rating >= n ? "text-yellow-400" : "text-white/20 hover:text-yellow-400/50")}>
              <Star className={clsx("w-6 h-6", rating >= n && "fill-yellow-400")} />
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-3 px-3 mt-3 mb-8 shrink-0">
        <button onClick={onRetake}
          className="py-3.5 rounded-2xl bg-white/10 border border-white/10 font-semibold text-sm hover:bg-white/20 transition-all flex items-center justify-center gap-2">
          <RotateCcw className="w-4 h-4" />
          Retake
        </button>
        <button onClick={onTryAnother}
          className="py-3.5 rounded-2xl bg-sxt-primary text-black font-bold text-sm hover:bg-sxt-primary/90 transition-all flex items-center justify-center gap-2">
          <Shirt className="w-4 h-4" />
          Try Another
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export function TryOnPage() {
  const videoRef     = useRef<HTMLVideoElement>(null);
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const skelCanvasRef = useRef<HTMLCanvasElement>(null);
  const pipelineRef  = useRef<ARPipeline | null>(null);
  const streamRef    = useRef<MediaStream | null>(null);

  // AR status
  const [arStatus, setArStatus] = useState<ARStatus>("initializing");
  const [arTier, setArTier] = useState<ARTier>(3);
  const [fps, setFps] = useState(0);
  const [showSkeleton, setShowSkeleton] = useState(false);

  // Camera
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Garments
  const [garments, setGarments] = useState<Garment[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selectedGarment = garments[selectedIdx];

  // Settings
  const [opacity, setOpacity] = useState(0.85);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);

  // Processing
  const [procState, setProcState] = useState<ProcessingState>("idle");
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [result, setResult] = useState<TryOnResult | null>(null);
  const [procError, setProcError] = useState<string | null>(null);

  // Drawer
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("mini");

  // AI Stylist — live backend data
  const [aiRecs, setAiRecs] = useState<AiOutfit[]>(AI_OUTFITS); // seed with local
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOccasion, setAiOccasion] = useState("casual");

  // Cloth physics
  const [clothEnabled, setClothEnabled] = useState(false);
  const [fabricPreset, setFabricPreset] = useState<"silk" | "cotton" | "denim" | "leather" | "knit">("cotton");

  // Timer
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Drawer heights
  const DRAWER_H: Record<DrawerMode, string> = {
    mini:     "5.5rem",
    garments: "46vh",
    ai:       "54vh",
    settings: "44vh",
  };

  // ── Static garment catalog — real images from sxtstyles.com ─────────────
  const CATALOG: Garment[] = [
    { id: 'g1',  name: 'Classic Dress Shirt',   category: 'Tops',      image_url: '/images/garments/real-shirt.jpg',    price_cents: 14500 },
    { id: 'g2',  name: 'Tailored Blazer',        category: 'Tops',      image_url: '/images/garments/real-blazer.jpg',   price_cents: 28900 },
    { id: 'g3',  name: 'Power Blazer',           category: 'Tops',      image_url: '/images/garments/real-blazer2.jpg',  price_cents: 32000 },
    { id: 'g4',  name: 'Silk Blouse',            category: 'Tops',      image_url: '/images/garments/real-blouse.jpg',   price_cents: 18500 },
    { id: 'g5',  name: 'Fitted Top',             category: 'Tops',      image_url: '/images/garments/real-top.jpg',      price_cents: 8900  },
    { id: 'g6',  name: 'Sport Polo',             category: 'Tops',      image_url: '/images/garments/real-polo.jpg',     price_cents: 11500 },
    { id: 'g7',  name: 'Premium Hoodie',         category: 'Tops',      image_url: '/images/garments/real-hoodie.jpg',   price_cents: 16500 },
    { id: 'g8',  name: 'Moto Jacket',            category: 'Outerwear', image_url: '/images/garments/real-jacket.jpg',   price_cents: 38900 },
    { id: 'g9',  name: 'Bomber Jacket',          category: 'Outerwear', image_url: '/images/garments/real-bomber.jpg',   price_cents: 29500 },
    { id: 'g10', name: 'Wool Overcoat',          category: 'Outerwear', image_url: '/images/garments/real-coat.jpg',     price_cents: 54900 },
    { id: 'g11', name: 'Oversized Cardigan',     category: 'Tops',      image_url: '/images/garments/real-cardigan.jpg', price_cents: 19500 },
    { id: 'g12', name: 'Tailored Vest',          category: 'Tops',      image_url: '/images/garments/real-vest.jpg',     price_cents: 15900 },
    { id: 'g13', name: 'Midi Dress',             category: 'Dresses',   image_url: '/images/garments/real-dress.jpg',    price_cents: 22900 },
    { id: 'g14', name: 'Statement Dress',        category: 'Dresses',   image_url: '/images/garments/real-dress2.jpg',   price_cents: 31500 },
    { id: 'g15', name: 'Midi Skirt',             category: 'Bottoms',   image_url: '/images/garments/real-skirt.jpg',    price_cents: 14900 },
    { id: 'g16', name: 'Power Suit Set',         category: 'Tops',      image_url: '/images/garments/real-suit.jpg',     price_cents: 62000 },
  ];

  // ── Load garments — try backend first, fall back to catalog ─────────────
  useEffect(() => {
    api.getProducts()
      .then((d: unknown) => {
        const items = Array.isArray(d) ? d : (d as { items?: Garment[] })?.items ?? [];
        // Use backend data if it has items, otherwise use the static catalog
        setGarments(items.length > 0 ? items as Garment[] : CATALOG);
      })
      .catch(() => api.getWardrobe()
        .then((d: unknown) => {
          const items = Array.isArray(d) ? d as Garment[] : [];
          setGarments(items.length > 0 ? items : CATALOG);
        })
        .catch(() => setGarments(CATALOG))
      );

    // Warm up MediaPipe in background (both layers)
    getPoseLandmarker().catch(() => {});
    getSegmenter().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Start camera + boot pipeline ──────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setCameraError(null);
    setCameraReady(false);

    // Stop existing pipeline + stream
    pipelineRef.current?.dispose();
    pipelineRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());

    try {
      // 720p on mobile — enough for pose detection, maintains 30fps
      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width:  { ideal: isMobile ? 720  : 1280 },
          height: { ideal: isMobile ? 1280 : 720  },
        },
        audio: false,
      });
      streamRef.current = stream;

      const video = videoRef.current!;
      video.srcObject = stream;
      await new Promise<void>(res => {
        video.onloadedmetadata = () => video.play().then(res);
      });

      // Boot Three.js pipeline
      const pipeline = new ARPipeline({
        canvas: canvasRef.current!,
        video,
        skeletonCanvas: skelCanvasRef.current ?? undefined,
        onTierChange: setArTier,
        onStatusChange: setArStatus,
        onFpsUpdate: setFps,
      });
      pipelineRef.current = pipeline;

      // Apply current settings
      pipeline.setSettings({ facingMode, isBottom: false, opacity, brightness });
      if (selectedGarment?.image_url) pipeline.setGarmentImage(selectedGarment.image_url);

      await pipeline.init();
      setCameraReady(true);
      // Auto-open garment drawer on first load so user knows what to do
      setDrawerMode(prev => prev === "mini" ? "garments" : prev);
    } catch {
      setCameraError("Camera access denied — allow camera in browser settings.");
    }
  }, [facingMode, opacity, brightness, selectedGarment]);

  // Boot on mount + facingMode change
  useEffect(() => {
    startCamera();
    return () => {
      pipelineRef.current?.dispose();
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [facingMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync garment to pipeline when selection changes ───────────────────────
  useEffect(() => {
    const p = pipelineRef.current;
    if (!p) return;
    if (selectedGarment?.image_url) {
      p.setGarmentImage(selectedGarment.image_url);
      p.setSettings({ isBottom: selectedGarment.category?.toLowerCase().includes("bottom") });
    } else {
      p.clearGarment();
    }
  }, [selectedGarment]);

  // ── Sync settings to pipeline ─────────────────────────────────────────────
  useEffect(() => {
    pipelineRef.current?.setSettings({ opacity, brightness, contrast, showSkeleton });
  }, [opacity, brightness, contrast, showSkeleton]);

  // ── Cloth physics — wire toggle + fabric preset ───────────────────────────
  useEffect(() => {
    const p = pipelineRef.current;
    if (!p) return;
    // hd=true tries ammo.js btSoftBody first, falls back to PBD automatically
    p.enableCloth(clothEnabled, fabricPreset, true);
  }, [clothEnabled, fabricPreset]);

  // ── Timer ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!cameraReady) return;
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [cameraReady]);

  const fmtTime = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  // ── Flip camera ───────────────────────────────────────────────────────────
  const flipCamera = useCallback(() => {
    setFacingMode(f => f === "user" ? "environment" : "user");
  }, []);

  // ── Capture snapshot + send to AI backend ────────────────────────────────
  const captureAndProcess = useCallback(async () => {
    if (!selectedGarment || !pipelineRef.current) return;
    const dataUrl = pipelineRef.current.snapshot();
    setCapturedImage(dataUrl);
    setProcState("scanning");
    setProcError(null);

    try {
      const scanResult = await api.startBodyScan("tryon-" + Date.now());
      setProcState("fitting");

      const res = await fetch(
        `${import.meta.env.VITE_API_URL || "https://sxt-platform-705725624713.us-central1.run.app"}/api/v1/tryon/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("sxt_token") || ""}`,
          },
          body: JSON.stringify({
            garment_id: selectedGarment.id,
            engine: "cloudflare",
            denoise_steps: 30,
            person_image: dataUrl,
            garment_image_url: selectedGarment.image_url,
            scan_id: (scanResult as { session_id?: string })?.session_id,
          }),
        }
      );

      let data: TryOnResult;
      if (!res.ok) {
        const wRes = await fetch("https://sxt-tryon-infer.wtdc202.workers.dev/tryon/infer", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: "sess_" + Date.now(),
            garment_id: selectedGarment.id,
            garment_image_url: selectedGarment.image_url,
            person_image: dataUrl,
            category: selectedGarment.category?.toLowerCase().includes("bottom") ? "lower_body" : "upper_body",
            size: "M",
          }),
        });
        const wData = await wRes.json() as { output?: TryOnResult };
        data = (wData?.output ?? wData) as TryOnResult;
      } else {
        data = await res.json() as TryOnResult;
      }

      setResult(data);
      setProcState("done");
    } catch {
      setProcError("Processing failed — check connection and try again.");
      setProcState("error");
    }
  }, [selectedGarment]);

  // ── Fetch live AI recommendations from backend ────────────────────────────
  const fetchAiRecs = useCallback(async (occasion: string) => {
    setAiLoading(true);
    try {
      const data = await api.getRecommendations() as {
        recommendations?: Array<{
          id: string;
          rationale: string;
          score: number;
          garments?: Array<{ name?: string; category?: string; color?: string }>;
          source?: string;
        }>;
      };
      const recs = data?.recommendations ?? [];
      if (recs.length > 0) {
        setAiRecs(recs.slice(0, 5).map((r, i) => ({
          emoji: ["✨", "🔥", "💼", "🌅", "🎯"][i % 5],
          title: r.garments?.[0]?.name ?? `Look ${i + 1}`,
          description: r.rationale || "AI-curated for your style",
          mood: occasion,
          tags: [
            r.garments?.[0]?.category ?? "top",
            r.garments?.[0]?.color ?? "neutral",
            r.source ?? "wardrobe",
          ].filter(Boolean),
          garmentId: r.garments?.[0] ? String(r.garments[0]) : undefined,
        })));
      }
    } catch {
      // Keep local mock outfits on error — already seeded
    } finally {
      setAiLoading(false);
    }
  }, []);

  // ── Retake ────────────────────────────────────────────────────────────────
  const retake = useCallback(() => {
    setProcState("idle");
    setCapturedImage(null);
    setResult(null);
    setProcError(null);
    setDrawerMode("mini");
    startCamera();
  }, [startCamera]);

  // ── Drawer toggle ─────────────────────────────────────────────────────────
  const toggleDrawer = (mode: DrawerMode) =>
    setDrawerMode(prev => prev === mode ? "mini" : mode);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black overflow-hidden select-none touch-none">

      {/* Hidden video */}
      <video ref={videoRef} className="hidden" playsInline muted autoPlay />

      {/* ══ FULL-SCREEN WebGL CANVAS ══ */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ imageRendering: "auto" }}
      />

      {/* ══ SKELETON OVERLAY CANVAS (2D, on top of WebGL) ══ */}
      <canvas
        ref={skelCanvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: showSkeleton ? 1 : 0, transition: "opacity 0.3s ease" }}
      />

      {/* Camera init — step-by-step loading */}
      {!cameraReady && !cameraError && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-6 bg-black px-8">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-10 h-10 animate-spin text-sxt-primary" />
            <p className="text-sm font-bold text-white">Starting AR Mirror</p>
          </div>
          <div className="w-full max-w-xs flex flex-col gap-2">
            {[
              { label: "Camera access",          done: true },
              { label: "Loading AR engine",       done: arStatus !== "initializing" },
              { label: "Pose detection (WASM)",   done: arTier <= 2 },
              { label: "Body segmentation",       done: arTier === 1 },
            ].map(({ label, done }) => (
              <div key={label} className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                  done ? "border-sxt-primary bg-sxt-primary" : "border-white/30"
                }`}>
                  {done && <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />}
                </div>
                <span className={`text-xs ${done ? "text-white" : "text-white/40"}`}>{label}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-white/30 text-center">
            First load downloads ~15MB of AI models.<br/>Subsequent loads are instant.
          </p>
        </div>
      )}

      {/* Camera error */}
      {cameraError && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-black px-8">
          <AlertCircle className="w-10 h-10 text-red-400" />
          <p className="text-sm text-red-400 text-center">{cameraError}</p>
          <button onClick={startCamera}
            className="px-6 py-3 bg-sxt-primary text-black font-bold rounded-2xl hover:bg-sxt-primary/90 transition-all">
            Retry Camera
          </button>
        </div>
      )}

      {/* ══ TOP HUD ══ */}
      {cameraReady && procState === "idle" && (
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 pt-4 pb-2 pointer-events-none">

          {/* Left: LIVE + timer + FPS */}
          <div className="flex items-center gap-2 pointer-events-auto">
            <div className="flex items-center gap-1.5 bg-black/50 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/10">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
              <span className="text-xs text-white font-bold tracking-widest">LIVE</span>
            </div>
            <div className="bg-black/40 backdrop-blur-md rounded-full px-2.5 py-1.5 border border-white/10">
              <span className="text-xs text-white/70 font-mono">{fmtTime(elapsed)}</span>
            </div>
            {fps > 0 && (
              <div className="flex items-center gap-1 bg-black/40 backdrop-blur-md rounded-full px-2 py-1.5 border border-white/10">
                <Activity className="w-3 h-3 text-green-400" />
                <span className="text-[10px] text-green-400 font-mono font-bold">{fps}</span>
              </div>
            )}
          </div>

          {/* Center: selected garment + AR tier */}
          <div className="flex flex-col items-center gap-1 pointer-events-auto">
            {selectedGarment ? (
              <button
                onClick={() => toggleDrawer("garments")}
                className="flex items-center gap-2 bg-black/50 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/10 max-w-[150px]"
              >
                {selectedGarment.image_url && (
                  <img src={selectedGarment.image_url} alt=""
                    className="w-4 h-4 rounded-full object-cover shrink-0" />
                )}
                <span className="text-xs text-white font-semibold truncate">{selectedGarment.name}</span>
              </button>
            ) : (
              <button
                onClick={() => toggleDrawer("garments")}
                className="flex items-center gap-1.5 bg-sxt-primary/20 backdrop-blur-md rounded-full px-3 py-1.5 border border-sxt-primary/40 animate-pulse"
              >
                <Shirt className="w-3 h-3 text-sxt-primary" />
                <span className="text-xs text-sxt-primary font-bold">Pick a garment ↓</span>
              </button>
            )}
            {/* AR tier badge */}
            <div className={clsx(
              "text-[9px] font-bold px-2 py-0.5 rounded-full border tracking-widest",
              TIER_LABEL[arTier].color
            )}>
              <Layers className="w-2.5 h-2.5 inline mr-1" />
              {TIER_LABEL[arTier].label}
            </div>
          </div>

          {/* Right: flip */}
          <button onClick={flipCamera}
            className="pointer-events-auto w-10 h-10 flex items-center justify-center bg-black/50 backdrop-blur-md rounded-full border border-white/10 hover:bg-white/10 transition-all">
            <RefreshCw className="w-4 h-4 text-white" />
          </button>
        </div>
      )}

      {/* ══ PROCESSING OVERLAY ══ */}
      {(procState === "scanning" || procState === "fitting") && (
        <div className="absolute inset-0 z-30 bg-black/75 backdrop-blur-md flex flex-col items-center justify-center gap-6">
          {capturedImage && (
            <div className="w-36 rounded-2xl overflow-hidden opacity-50 relative">
              <img src={capturedImage} alt="captured" className="w-full object-cover" />
              <div className="absolute inset-0 bg-sxt-primary/20 animate-pulse" />
            </div>
          )}
          <Loader2 className="w-10 h-10 animate-spin text-sxt-primary" />
          <div className="text-center space-y-1">
            <p className="text-base font-bold">
              {procState === "scanning" ? "Scanning body shape…" : "Fitting garment to your mesh…"}
            </p>
            <p className="text-sm text-sxt-muted">
              {procState === "scanning" ? "Body landmarks detected · extracting mesh" : "Warping garment to skeleton joints"}
            </p>
          </div>
          {selectedGarment && (
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-2xl px-4 py-2.5 border border-white/10">
              {selectedGarment.image_url && (
                <img src={selectedGarment.image_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
              )}
              <span className="text-sm font-semibold">{selectedGarment.name}</span>
              <Zap className="w-4 h-4 text-sxt-primary" />
            </div>
          )}
        </div>
      )}

      {/* Processing error */}
      {procState === "error" && procError && (
        <div className="absolute top-20 left-4 right-4 z-30 flex items-center gap-3 bg-red-500/15 border border-red-500/30 rounded-2xl px-4 py-3">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-sm text-red-300 flex-1">{procError}</span>
          <button onClick={retake} className="text-xs text-red-400 underline shrink-0">Retry</button>
        </div>
      )}

      {/* ══ RESULT OVERLAY ══ */}
      {procState === "done" && result && capturedImage && (
        <ResultOverlay
          capturedImage={capturedImage}
          result={result}
          selectedGarment={selectedGarment}
          onRetake={retake}
          onTryAnother={() => { retake(); setDrawerMode("garments"); }}
        />
      )}

      {/* ══════════════════════════════════════════════════════
          FLOATING BOTTOM DRAWER — AR FASHION OS CONTROL BAR
      ══════════════════════════════════════════════════════ */}
      {(procState === "idle" || procState === "error") && (
        <div
          className="absolute bottom-0 left-0 right-0 z-20"
          style={{
            height: DRAWER_H[drawerMode],
            transition: "height 0.38s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {/* Glass panel */}
          <div className="h-full flex flex-col bg-black/65 backdrop-blur-2xl border-t border-white/10 rounded-t-[28px] overflow-hidden">

            {/* Drag handle */}
            <div
              className="flex justify-center pt-3 pb-1 shrink-0 cursor-pointer"
              onClick={() => setDrawerMode(m => m === "mini" ? "garments" : "mini")}
            >
              <div className="w-9 h-[3px] rounded-full bg-white/20" />
            </div>

            {/* ── MINI CONTROL ROW — always visible ── */}
            <div className="flex items-center justify-between px-3 py-2 shrink-0 gap-2">

              {/* Left tab cluster */}
              <div className="flex items-center gap-1">
                {([
                  { mode: "garments" as DrawerMode, Icon: Shirt,             label: "Fits"     },
                  { mode: "ai"       as DrawerMode, Icon: Sparkles,          label: "AI"       },
                  { mode: "settings" as DrawerMode, Icon: SlidersHorizontal, label: "Settings" },
                ] as const).map(({ mode, Icon, label }) => (
                  <button
                    key={mode}
                    onClick={() => toggleDrawer(mode)}
                    className={clsx(
                      "flex items-center gap-1.5 px-3 py-2 rounded-2xl text-xs font-bold transition-all",
                      drawerMode === mode
                        ? "bg-sxt-primary text-black shadow-lg shadow-sxt-primary/20"
                        : "bg-white/10 text-white/60 hover:bg-white/15 hover:text-white"
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span className="hidden xs:inline">{label}</span>
                  </button>
                ))}
              </div>

              {/* Centre: shutter */}
              <button
                onClick={captureAndProcess}
                disabled={!selectedGarment || !cameraReady}
                className={clsx(
                  "w-[68px] h-[68px] rounded-full border-[3px] flex items-center justify-center transition-all shadow-2xl shrink-0",
                  selectedGarment && cameraReady
                    ? "border-white/40 bg-sxt-primary hover:bg-sxt-primary/90 active:scale-95 shadow-sxt-primary/25"
                    : "border-white/10 bg-white/10 opacity-40 cursor-not-allowed"
                )}
              >
                <Aperture className="w-7 h-7 text-black" />
              </button>

              {/* Right: flip + prev/next */}
              <div className="flex items-center gap-1">
                <button onClick={flipCamera}
                  className="p-2.5 rounded-2xl bg-white/10 text-white/60 hover:bg-white/15 hover:text-white transition-all">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setSelectedIdx(i => Math.max(0, i - 1))}
                  disabled={selectedIdx === 0 || garments.length === 0}
                  className="p-2.5 rounded-2xl bg-white/10 text-white/60 hover:bg-white/15 disabled:opacity-20 transition-all">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setSelectedIdx(i => Math.min(garments.length - 1, i + 1))}
                  disabled={selectedIdx >= garments.length - 1}
                  className="p-2.5 rounded-2xl bg-white/10 text-white/60 hover:bg-white/15 disabled:opacity-20 transition-all">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* ── EXPANDED CONTENT ── */}
            <div
              className="flex-1 overflow-hidden"
              style={{
                opacity: drawerMode === "mini" ? 0 : 1,
                transform: drawerMode === "mini" ? "translateY(8px)" : "translateY(0)",
                transition: "opacity 0.25s ease, transform 0.25s ease",
                pointerEvents: drawerMode === "mini" ? "none" : "auto",
              }}
            >

              {/* ── GARMENTS PANEL ── */}
              {drawerMode === "garments" && (
                <div className="h-full flex flex-col px-4 pb-3 gap-3 overflow-hidden">
                  <div className="flex items-center justify-between shrink-0">
                    <p className="text-[10px] font-bold text-white/40 uppercase tracking-[0.12em]">
                      Wardrobe · {garments.length} items
                    </p>
                    <button onClick={() => setDrawerMode("mini")}>
                      <ChevronDown className="w-4 h-4 text-white/30" />
                    </button>
                  </div>
                  {garments.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-sxt-primary" />
                    </div>
                  ) : (
                    <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-none flex-1 items-start content-start">
                      {garments.map((g, i) => (
                        <GarmentThumb key={g.id} garment={g} selected={selectedIdx === i}
                          onSelect={() => setSelectedIdx(i)} />
                      ))}
                    </div>
                  )}
                  {selectedGarment && (
                    <div className="shrink-0 bg-white/5 rounded-2xl px-3 py-2.5 flex items-center gap-3 border border-white/10">
                      {selectedGarment.image_url && (
                        <img src={selectedGarment.image_url} alt=""
                          className="w-10 h-10 rounded-xl object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate">{selectedGarment.name}</p>
                        <p className="text-xs text-sxt-muted">{selectedGarment.brand || selectedGarment.category}</p>
                      </div>
                      {selectedGarment.price_cents && (
                        <span className="text-sm font-bold text-sxt-primary shrink-0">
                          ${(selectedGarment.price_cents / 100).toFixed(0)}
                        </span>
                      )}
                      <div className="w-6 h-6 bg-sxt-primary rounded-full flex items-center justify-center shrink-0">
                        <Check className="w-3.5 h-3.5 text-black" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── AI STYLE PANEL — live backend ── */}
              {drawerMode === "ai" && (
                <div className="h-full flex flex-col px-4 pb-3 gap-3 overflow-hidden">
                  <div className="flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-sxt-primary" />
                      <p className="text-[10px] font-bold text-white/40 uppercase tracking-[0.12em]">AI Stylist · GPT-4o</p>
                      {aiLoading && <Loader2 className="w-3 h-3 animate-spin text-sxt-primary" />}
                    </div>
                    <button onClick={() => setDrawerMode("mini")}>
                      <ChevronDown className="w-4 h-4 text-white/30" />
                    </button>
                  </div>

                  {/* Occasion selector */}
                  <div className="flex gap-1.5 shrink-0 overflow-x-auto scrollbar-none">
                    {["casual", "work", "date", "gym", "evening", "travel"].map(occ => (
                      <button key={occ}
                        onClick={() => { setAiOccasion(occ); fetchAiRecs(occ); }}
                        className={clsx(
                          "shrink-0 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all border",
                          aiOccasion === occ
                            ? "bg-sxt-primary text-black border-sxt-primary"
                            : "bg-white/5 text-white/40 border-white/10 hover:bg-white/10 hover:text-white"
                        )}>
                        {occ}
                      </button>
                    ))}
                  </div>

                  {/* Outfit cards */}
                  <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-none flex-1 items-start">
                    {aiRecs.map(outfit => (
                      <AiCard key={outfit.title} outfit={outfit}
                        onApply={() => setDrawerMode("garments")} />
                    ))}
                  </div>

                  {/* Action buttons — now wired */}
                  <div className="grid grid-cols-3 gap-2 shrink-0">
                    {[
                      { label: "Style Me",      occasion: aiOccasion },
                      { label: "Match Colors",  occasion: "color-match" },
                      { label: "Surprise Me",   occasion: "random" },
                    ].map(({ label, occasion }) => (
                      <button key={label}
                        onClick={() => fetchAiRecs(occasion)}
                        disabled={aiLoading}
                        className="py-2.5 rounded-2xl bg-white/5 border border-white/10 text-xs font-semibold text-white/60 hover:bg-sxt-primary/10 hover:text-sxt-primary hover:border-sxt-primary/30 disabled:opacity-40 transition-all">
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── SETTINGS PANEL ── */}
              {drawerMode === "settings" && (
                <div className="h-full flex flex-col px-4 pb-3 gap-4 overflow-hidden">
                  <div className="flex items-center justify-between shrink-0">
                    <p className="text-[10px] font-bold text-white/40 uppercase tracking-[0.12em]">Render Settings</p>
                    <button onClick={() => setDrawerMode("mini")}>
                      <ChevronDown className="w-4 h-4 text-white/30" />
                    </button>
                  </div>

                  {/* AR tier indicator */}
                  <div className="flex items-center gap-3 bg-white/5 rounded-2xl px-3 py-2.5 border border-white/10 shrink-0">
                    <Layers className="w-4 h-4 text-sxt-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold">
                        {TIER_LABEL[arTier].label}
                        {arTier === 1 && " · Pose + Occlusion + WebGL"}
                        {arTier === 2 && " · Pose + WebGL (no occlusion)"}
                        {arTier === 3 && " · Heuristic placement"}
                      </p>
                      <p className="text-[10px] text-sxt-muted">
                        {arTier === 3 ? "MediaPipe loading…" : "MediaPipe active"}
                      </p>
                    </div>
                    <div className={clsx(
                      "w-2 h-2 rounded-full shrink-0",
                      arTier === 1 ? "bg-green-400" : arTier === 2 ? "bg-yellow-400" : "bg-orange-400"
                    )} />
                  </div>

                  {/* Sliders */}
                  {[
                    { label: "Garment Overlay", Icon: Shirt,    value: Math.round(opacity * 100), min: 20, max: 100, step: 5, color: "accent-sxt-primary",  setter: (v: number) => setOpacity(v / 100) },
                    { label: "Brightness",       Icon: Sun,     value: brightness,                min: 60, max: 160, step: 5, color: "accent-yellow-400",   setter: setBrightness },
                    { label: "Contrast",         Icon: Contrast, value: contrast,                min: 60, max: 160, step: 5, color: "accent-blue-400",     setter: setContrast },
                  ].map(({ label, Icon, value, min, max, step, color, setter }) => (
                    <div key={label} className="space-y-1.5 shrink-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5 text-white/50" />
                          <span className="text-xs text-white/60">{label}</span>
                        </div>
                        <span className="text-xs font-bold text-white/80">{value}{label === "Garment Overlay" ? "%" : "%"}</span>
                      </div>
                      <input type="range" min={min} max={max} step={step} value={value}
                        onChange={e => setter(Number(e.target.value))}
                        className={clsx("w-full h-1.5 rounded-full", color)} />
                    </div>
                  ))}

                  {/* Cloth Physics toggle (O3DE replacement) */}
                  <div className="flex items-center justify-between shrink-0 bg-white/5 rounded-2xl px-3 py-2.5 border border-white/10">
                    <div className="flex items-center gap-2">
                      <Wind className={clsx("w-3.5 h-3.5", clothEnabled ? "text-blue-400" : "text-white/40")} />
                      <div>
                        <p className="text-xs font-semibold">Cloth Physics</p>
                        <p className="text-[10px] text-white/30">Rapier3D PBD simulation</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setClothEnabled(e => !e)}
                      className={clsx(
                        "w-10 h-5 rounded-full transition-all relative shrink-0",
                        clothEnabled ? "bg-blue-500" : "bg-white/20"
                      )}
                    >
                      <div className={clsx(
                        "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all",
                        clothEnabled ? "left-[calc(100%-18px)]" : "left-0.5"
                      )} />
                    </button>
                  </div>

                  {/* Fabric preset (when cloth enabled) */}
                  {clothEnabled && (
                    <div className="shrink-0">
                      <p className="text-[10px] text-white/30 mb-2 uppercase tracking-widest">Fabric Type</p>
                      <div className="grid grid-cols-5 gap-1.5">
                        {(["silk", "cotton", "denim", "leather", "knit"] as const).map(f => (
                          <button key={f}
                            onClick={() => setFabricPreset(f)}
                            className={clsx(
                              "py-2 rounded-xl text-[10px] font-bold capitalize transition-all border",
                              fabricPreset === f
                                ? "bg-blue-500/20 border-blue-400 text-blue-300"
                                : "bg-white/5 border-white/10 text-white/40 hover:bg-white/10"
                            )}>
                            {f}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skeleton overlay toggle (Phase 1 milestone) */}
                  <div className="flex items-center justify-between shrink-0 bg-white/5 rounded-2xl px-3 py-2.5 border border-white/10">
                    <div className="flex items-center gap-2">
                      {showSkeleton ? <Eye className="w-3.5 h-3.5 text-sxt-primary" /> : <EyeOff className="w-3.5 h-3.5 text-white/40" />}
                      <div>
                        <p className="text-xs font-semibold">Skeleton Overlay</p>
                        <p className="text-[10px] text-white/30">33-joint pose visualization</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowSkeleton(s => !s)}
                      className={clsx(
                        "w-10 h-5 rounded-full transition-all relative shrink-0",
                        showSkeleton ? "bg-sxt-primary" : "bg-white/20"
                      )}
                    >
                      <div className={clsx(
                        "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all",
                        showSkeleton ? "left-[calc(100%-18px)]" : "left-0.5"
                      )} />
                    </button>
                  </div>

                  {/* Camera mode */}
                  <div className="shrink-0">
                    <p className="text-[10px] text-white/30 mb-2 uppercase tracking-widest">Camera</p>
                    <div className="grid grid-cols-2 gap-2">
                      {(["user", "environment"] as const).map(mode => (
                        <button key={mode}
                          onClick={() => { if (facingMode !== mode) flipCamera(); }}
                          className={clsx(
                            "py-2.5 rounded-2xl text-xs font-semibold transition-all border",
                            facingMode === mode
                              ? "bg-sxt-primary/15 border-sxt-primary text-sxt-primary"
                              : "bg-white/5 border-white/10 text-white/40 hover:bg-white/10"
                          )}>
                          {mode === "user" ? "Front · Selfie" : "Rear · Natural"}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
